export interface OperatorPhoneReport {
  sendingReport?: {capturedAt:string; items:PhoneSendingItem[]} | null;
  storageDiagnostics?: unknown;
  storageCycleNames?: Record<string,string> | null;
  offlineMediaBytes?: number | null;
  workStoreNames?: Record<string,string> | null;
  workCycleNames?: Record<string,string> | null;
  workMetrics?: unknown;
  previousWorkReport?: {createdAt: string;metrics: unknown} | null;
  osName?: string | null;
  osVersion?: string | null;
  appVersion?: string | null;
  dbSizeMb?: number | null;
  offlineMediaFileCount?: number | null;
  outboxSuccessRowCount?: number | null;
  lastLocalRetentionAt?: string | null;
  storageTelemetryReadOk?: boolean | null;
  operatorId: number;
  createdAt: string | null;
  networkState: string | null;
  unsafeCount: number | null;
  unsafeStatusReadOk: boolean | null;
  unsafeCounts: { failed?: number; pending?: number; conflict?: number; processing?: number; failed_permanent?: number } | null;
  protectedEvidenceMissingStoreCount: number | null;
}

export type PhoneSendingItem = {
  operationId:string; assignmentId:number|null; storeId:number|null; cycleId:number|null;
  eventType:string; status:string; attempts:number; createdAt:number; lastAttemptAt:number|null; error:string|null;
};

export function formatPhoneReportTime(timestamp: string | null | undefined) {
  const date = timestamp ? new Date(timestamp) : null;
  return date && Number.isFinite(date.getTime())
    ? `${new Intl.DateTimeFormat('en-IE', { timeZone: 'Europe/Dublin', dateStyle: 'medium', timeStyle: 'short' }).format(date)} Irish time`
    : 'No valid report time';
}

export function describeOperatorDelivery(report: OperatorPhoneReport | undefined, now = Date.now()) {
  const timestamp = report?.createdAt ? Date.parse(report.createdAt) : NaN;
  if (!report) return { label: 'No phone report available', tone: 'muted', explanation: 'Phone delivery status is unknown. Server work and signature records remain available.', action: 'Ask the operator to open ReActor online for a fresh report' };
  if (!Number.isFinite(timestamp) || timestamp > now + 60_000 || now - timestamp > 15 * 60_000) {
    return { label: 'Phone report out of date', tone: 'muted', explanation: 'We need a fresh phone report to confirm its current sending status. Earlier warnings may have changed.', action: 'Ask the operator to open ReActor online for a fresh report' };
  }
  if (report.unsafeStatusReadOk !== true || report.unsafeCount == null) {
    return { label: 'Phone could not report delivery', tone: 'muted', explanation: 'The latest report could not reliably check saved work.', action: 'Open delivery details' };
  }
  if ((report.protectedEvidenceMissingStoreCount ?? 0) > 0) {
    return { label: 'Office action needed', tone: 'danger', explanation: 'The latest report includes photos not matched to a store. They may belong to another cycle.', action: 'Open delivery details to identify the affected work' };
  }
  if ((report.unsafeCounts?.failed_permanent ?? 0) > 0) {
    return { label: 'Office action needed', tone: 'danger', explanation: 'The latest report flags saved items for office investigation. They may belong to another cycle.', action: 'Open delivery details to check current server receipts before taking action' };
  }
  if (report.unsafeCount === 0) {
    return { label: 'Phone reports nothing waiting', tone: 'good', explanation: 'The latest phone report has no outstanding delivery items. Evidence still needs compliance review.', action: 'Open review queue' };
  }
  if ((report.unsafeCounts?.conflict ?? 0) > 0) {
    return { label: 'Delivery needs checking', tone: 'warning', explanation: 'The phone reported saved items that could not be matched with server records. Check current delivery details; recovery may have progressed since this report.', action: 'Open delivery details' };
  }
  if ((report.unsafeCounts?.failed ?? 0) > 0) {
    return { label: 'Sending delayed', tone: 'warning', explanation: `${report.unsafeCount} saved items were awaiting delivery. Failed attempts do not establish the cause or mean the operator must redo the work. The app retries automatically when it can sync.`, action: 'Ask the operator to open ReActor online and tap Try Sending Now' };
  }
  return { label: report.networkState === 'offline' ? 'Saved work waiting for connection' : (report.unsafeCounts?.processing ?? 0) > 0 ? 'Phone reported sending work' : 'Saved work waiting to send', tone: 'warning', explanation: `${report.unsafeCount} saved items were awaiting delivery in the latest report. The app sends saved work automatically when it can sync.`, action: 'Ask the operator to keep ReActor open online' };
}
